import { useMemo } from 'react';
import { useForm } from '@common-fe/common-fe';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import _ from 'lodash';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { DEFAULT_DATE_FORMAT, REQUIRED_TEXT } from '@/common/constants';
import { AccumulatedItem } from '@/modules/deductibles/SpendTracker/hooks/useDeductibles.ts';

export enum DeductibleFormField {
  effectiveDate = 'effectiveDate',
  familyDeductible = 'familyDeductible',
  memberDeductible = 'memberDeductible',
}

export type DeductibleFormFields = {
  effectiveDate: string;
  familyDeductible: string;
  memberDeductible: Record<string, string | undefined>;
};

const getDefaultMemberDeductible = (
  memberKeys: string[],
  individualAccumulatedValues?: AccumulatedItem[],
) => {
  const map: Record<string, string> = {};
  memberKeys.forEach((key) => {
    let item = individualAccumulatedValues?.find((i) => _.toString(i.dependentId) === key);
    if (!item) {
      item = individualAccumulatedValues?.find((i) => _.toString(i.employeeId) === key && i.dependentId == null);
    }
    map[key] = _.toString(item?.value);
  });
  return map;
};

const getSchema = (memberKeys: string[]) =>
  yup.object({
    effectiveDate: VALIDATORS.DATE_FORMAT.required(REQUIRED_TEXT),
    familyDeductible: VALIDATORS.REQUIRED_STRING,
    memberDeductible: yup.object(
      memberKeys.reduce((acc, key) => {
        acc[key] = VALIDATORS.REQUIRED_STRING;
        return acc;
      }, {} as Record<string, yup.StringSchema>)
    ),
  });

const useDeductibleForm = (
  individualAccumulatedValues?: AccumulatedItem[],
  familyValue?: number,
) => {
const memberKeys = useMemo(
  () =>
    (individualAccumulatedValues ?? []).map(
      (item) =>
        item.dependentId != null
          ? _.toString(item.dependentId)
          : _.toString(item.employeeId)
    ),
  [individualAccumulatedValues]
);
  const defaultMemberDeductible = getDefaultMemberDeductible(memberKeys, individualAccumulatedValues);
  const defaultValues = useMemo(() => ({
    effectiveDate: dayjs(new Date()).format(DEFAULT_DATE_FORMAT),
    familyDeductible: _.toString(familyValue),
    memberDeductible: defaultMemberDeductible,
  }), [familyValue, defaultMemberDeductible]);

  const methods = useForm<DeductibleFormFields>({
    mode: 'onSubmit',
    defaultValues,
    resolver: yupResolver(getSchema(memberKeys)),
  });

  return { methods, defaultValues };
};

export default useDeductibleForm;
